import * as React from 'react'
import {useEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {Badge} from '@components/common/base/badge'
import {Icon} from '@components/common/base/icon'
import {TextLoader} from '@components/common/base/text-loader'
import {IdTaskText} from '@components/id-tasks/id-task-text'
import {useForceUpdate} from '@containers/hooks'
import {ChatProxy} from '@extensions/proxies/chat-proxy'
import {Search, SearchResultItemType} from '@extensions/search'

const TwBadge = tw.div`
  flex
  gap-4
  items-center
  ml-auto
`

type EntityType = 'group' | 'task' | 'personal'
export type LocationType = 'search' | 'right-panel'

interface Props extends ChatProxy {
  readonly titleChildren?: React.ReactNode
  readonly withMe?: boolean
  readonly complete?: number
  highlightText?: string
  highlightType?: SearchResultItemType
  starred?: boolean
  type: EntityType
  locationType: LocationType
  startFrom?: number
  endAt?: number
  isDeal?: boolean
  isCrmDefaultFieldsHub?: 'contact' | 'organization' | undefined
  isLazy?: boolean
}

const TwDealBadge = tw.span`
  inline-flex
  items-center
  justify-center
  bg-background-secondary
  text-secondary
  px-4
  rounded-4
  text-xs-semibold
  uppercase
`

export const ChatInfo = (props: Props) => {
  const {
    chatName,
    lastMessageTimestamp,
    createdTimestamp,
    newMessagesAmount,
    markImportant,
    getNewMentionsAmount,
    lastMessageText = '',
    lastMessageAuthor = '',
    titleChildren,
    withMe = true,
    complete = 0,
    highlightText = '',
    starred = false,
    highlightType,
    startFrom = 0,
    endAt = Number.MAX_VALUE,
    isDeal = false,
    isCrmDefaultFieldsHub = undefined,
    isLazy
  } = props

  const newMentionsAmountRef = useRef(0)
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    if (newMessagesAmount > 0) {
      newMentionsAmountRef.current = getNewMentionsAmount?.()
      forceUpdate()
    }
  }, [newMessagesAmount])

  const bem = Util.bemFrom('chat-info')

  const timestamp = Time.readableTimestamp(
    lastMessageTimestamp ? lastMessageTimestamp : createdTimestamp
  )
  const h = (txt: string, types: SearchResultItemType[]) =>
    highlightType && types.includes(highlightType)
      ? highlight(highlightText, txt, startFrom, endAt)
      : txt

  const showIdTask = props.type === 'task' && props.locationType === 'search'

  return (
    <div className={bem()}>
      <div
        className={bem('name', {
          newMessages: newMessagesAmount > 0,
          muted: !withMe
        })}
      >
        {starred && <div className={bem('star')} />}

        {(isDeal || isCrmDefaultFieldsHub) && (
          <TwDealBadge>
            {isCrmDefaultFieldsHub === 'contact' && Lang.msg.crm.contactBadge}
            {isCrmDefaultFieldsHub === 'organization' &&
              Lang.msg.crm.organizationBadge}
            {!isCrmDefaultFieldsHub && Lang.msg.crm.dealBadge}
          </TwDealBadge>
        )}

        {!!complete && (
          <div className={bem('completed-hint')}>
            [{Lang.msg.taskCompleted}]
          </div>
        )}
        <span>
          {isLazy && <TextLoader />}
          {!isLazy &&
            h(chatName, ['group-chat-title', 'task-title', 'user-name'])}
        </span>
        {showIdTask && (
          <IdTaskText
            taskId={props.chatId}
            highlighted={highlightType === 'id'}
          />
        )}
        {titleChildren}
      </div>
      <div className={bem('last-message', {muted: !withMe})}>
        <span className="overflow-hidden flex-1">
          {lastMessageText && (
            <>
              {lastMessageAuthor && (
                <>{h(lastMessageAuthor, ['user-name'])}: </>
              )}
              {h(lastMessageText, [
                'id',
                'message',
                'description',
                'checklist-title',
                'checklist-item'
              ])}
            </>
          )}
          {!lastMessageText && Lang.msg.noMessages}
        </span>
        {newMessagesAmount === 0 && !!markImportant && (
          <Icon icon="IconCustomAlertCircle" size={16} color={'error-l20'} />
        )}

        {!withMe && newMessagesAmount === 0 && <div className={bem('muted')} />}
        {newMessagesAmount > 0 && (
          <TwBadge>
            {!!markImportant && (
              <Icon
                icon="IconCustomAlertCircle"
                size={16}
                color={'error-l20'}
              />
            )}

            {newMentionsAmountRef.current > 0 && (
              <Icon icon="IconAt" size={16} color={'error-l20'} />
            )}

            {
              <Badge type="rounded" unread={withMe}>
                {newMessagesAmount}
              </Badge>
            }
          </TwBadge>
        )}
      </div>
      <div className={bem('timestamp')}>{timestamp}</div>
    </div>
  )
}

const bounds = 12

const highlight = (
  query: string,
  text: string,
  startFrom = 0,
  endAt = Number.MAX_VALUE
): JSX.Element => {
  const elems: JSX.Element[] = []
  startFrom = startFrom > bounds ? startFrom - bounds : 0
  endAt = endAt + bounds
  const prefix = startFrom ? '…' : ''
  const suffix = endAt < text.length ? '…' : ''

  Search.highlight(
    query,
    prefix + text.slice(startFrom, endAt) + suffix,
    (plain, isFirst, isLast) => {
      let content = plain
      if (isFirst && plain.length > bounds) {
        content = '… ' + plain.slice(-bounds)
      } else if (isLast && plain.length > bounds) {
        content = plain.slice(0, bounds) + ' …'
      }
      elems.push(<React.Fragment key={elems.length}>{content}</React.Fragment>)
    },
    hl =>
      elems.push(
        <span key={elems.length} className="text-action-default">
          {hl}
        </span>
      )
  )
  return <>{elems}</>
}
